import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService, DovsMatchPreview } from '../../shared/services/api.service';
import { AuthService } from '../../core/auth/auth.service';
import { maskIdNumberForDisplay } from '../../shared/utils/id-number-display';

@Component({
  selector: 'app-digital-onboarding-wizard',
  templateUrl: './digital-onboarding-wizard.component.html',
  styleUrls: ['./digital-onboarding-wizard.component.css'],
})
export class DigitalOnboardingWizardComponent implements OnInit {
  step = 1;
  form: FormGroup;
  matching = false;
  starting = false;
  matchSessionId: string | null = null;
  preview: DovsMatchPreview | null = null;
  successMessage: string | null = null;
  private isAdmin = false;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private snackBar: MatSnackBar,
    private router: Router,
    private auth: AuthService
  ) {
    this.form = this.fb.group({
      idNumber: ['', [Validators.required, Validators.pattern(/^\d{13}$/)]],
      cellNumber: ['', [Validators.required, Validators.pattern(/^0\d{9}$/)]],
      yourReference: [''],
    });
  }

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe((user) => {
      this.isAdmin = user?.role === 'admin';
    });
  }

  get basePath(): string {
    return this.isAdmin ? '/admin/digital-onboarding' : '/user/digital-onboarding';
  }

  backToList(): void {
    this.router.navigate([this.basePath]);
  }

  runMatch(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.matching = true;
    const value = this.form.value;
    this.api
      .dovsMatch({
        idNumber: String(value.idNumber).trim(),
        cellNumber: String(value.cellNumber).trim(),
        yourReference: value.yourReference?.trim() || undefined,
        requestPurpose: 'digital_onboarding',
      })
      .subscribe({
        next: (res) => {
          this.matching = false;
          if (res.resultCode === 'NO_MATCH' || !res.matchSessionId || !res.preview) {
            this.snackBar.open('No consumer match found for that ID and cell number', 'Close', {
              duration: 5000,
            });
            return;
          }
          this.matchSessionId = res.matchSessionId;
          this.preview = res.preview;
          this.step = 2;
        },
        error: (err) => {
          this.matching = false;
          this.snackBar.open(
            err?.error?.error?.message || 'Match failed',
            'Close',
            { duration: 5000 }
          );
        },
      });
  }

  confirmStart(): void {
    if (!this.matchSessionId) return;
    this.starting = true;
    const value = this.form.value;
    this.api
      .dovsStart({
        matchSessionId: this.matchSessionId,
        idNumber: String(value.idNumber).trim(),
        cellNumber: String(value.cellNumber).trim(),
        requestPurpose: 'digital_onboarding',
      })
      .subscribe({
        next: (res) => {
          this.starting = false;
          this.successMessage = res.message || `SMS sent to ${res.cellNumberMasked}`;
          this.step = 3;
        },
        error: (err) => {
          this.starting = false;
          this.snackBar.open(
            err?.error?.error?.message || 'Failed to send verification SMS',
            'Close',
            { duration: 5000 }
          );
        },
      });
  }

  cancelMatch(): void {
    this.matchSessionId = null;
    this.preview = null;
    this.step = 1;
  }

  formatId(value: string | null | undefined): string {
    return maskIdNumberForDisplay(value) || '—';
  }
}
